import { useSyncExternalStore } from 'react';
import { useAppSelector } from './redux';
import {
  resolveStocker2DDisplayCounts,
  type Stocker2DDisplayCounts,
} from '../features/simulation/stocker2dDisplayCounts';
import { liveInventoryDisplayStore } from '../store/liveInventoryDisplayStore';
import { liveStreamInventoryStore } from '../store/liveStreamInventoryStore';
import { liveOhtRuntimeStore } from '../store/liveOhtRuntimeStore';
import { liveMotionStore } from '../store/liveMotionStore';
import { subscribeStocker2dDisplayRefresh } from '../utils/stocker2dDisplaySubscribe';
import { subscribeThrottled } from '../utils/throttledStoreSubscribe';
import type { Port } from '../utils/ports';

/** Timeline / column-tag coalesce — transfer progress uses frame bus. */
const STOCKER_DISPLAY_NOTIFY_MS = 50;

/**
 * Live 2D stocker dots from streamed transfer events (LP-timed), not raw
 * inventory frames alone.
 */
export function useStocker2DDisplayCounts(
  elementId: string | undefined,
  aliases: readonly string[],
  layoutPorts: readonly Port[],
  totalSlots: number,
  enabled: boolean
): Stocker2DDisplayCounts | undefined {
  const elements = useAppSelector((state) => state.layout.elements);
  const stockerPortOwners = useAppSelector(
    (state) => state.simulation.browserKinetic?.stockerPortOwners
  );

  return useSyncExternalStore(
    (onStoreChange) => {
      if (!enabled || !elementId) return () => {};
      const unsubs = [
        subscribeStocker2dDisplayRefresh(onStoreChange),
        subscribeThrottled(onStoreChange, STOCKER_DISPLAY_NOTIFY_MS, [
          (notify) => liveMotionStore.subscribe(notify),
        ]),
      ];
      return () => {
        for (const unsub of unsubs) unsub();
      };
    },
    () => {
      if (!enabled || !elementId) return undefined;
      return resolveStocker2DDisplayCounts({
        stockerKey: elementId,
        elementId,
        aliases,
        layoutPorts,
        stockerPortOwners,
        elements,
        inventory: liveInventoryDisplayStore.getSnapshot(),
        rawInventory: liveStreamInventoryStore.getSnapshot(),
        runtimeFrame: liveOhtRuntimeStore.getFrame(),
        ohtTimelines: liveMotionStore.getOhtTimelines(),
        totalSlots: totalSlots,
      });
    },
    () => undefined
  );
}
